import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

import PrecisionService from 'src/core/services/PrecisionService';
import {
  useCreateTaxationPlanSpecific,
  useGetActiveLinePlans,
  useGetCurrencies,
} from '@common/slices';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';
import {
  IBaseDrawerProps,
  IIdCodeName,
  NewPlanSpecificTaxationFormValues
} from '@common/types';
import { newPlanSpecificTaxationDrawerSections } from '@common/constants';

const getInitialValues = (
  lineID?: string
): NewPlanSpecificTaxationFormValues => ({
  lineID: lineID ? [lineID] : [],
  planID: [],
  fixedStamp: '',
  fixedStampCurrency: [],
  proportionalStamp: '',
  municipalityTax: '',
  taxOnCommission: '',
  reinsuranceTax: ''
});

interface Props extends IBaseDrawerProps {
  taxationId: string;
  taxationLine: IIdCodeName;
}

const NewPlanSpecificTaxationDrawer: React.FC<Props> = ({
  taxationId,
  taxationLine,
  onSuccess
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const { data: currenciesResp } = useGetCurrencies.useQuery();
  const [getPlansByLine] = useGetActiveLinePlans.useLazy();
  const [createPlanSpecific] = useCreateTaxationPlanSpecific();

  const lineOptions = useMemo<Record<string, string>>(() => {
    if (!taxationLine?.id) return {};
    return { [taxationLine.id]: `${taxationLine.code} - ${taxationLine.name}` };
  }, [taxationLine]);

  const currencyOptions = useMemo<Record<string, string>>(() => {
    const list = currenciesResp?.core?.getCurrencies ?? [];
    return list.reduce((acc: Record<string, string>, c: any) => {
      acc[c.id] = c.title;
      return acc;
    }, {});
  }, [currenciesResp]);

  const [planOptions, setPlanOptions] = useState<Record<string, string>>({});

  const sections = useMemo<IDynamicFormWidgetSection[]>(() => {
    const initial = getInitialValues(taxationLine?.id);
    return newPlanSpecificTaxationDrawerSections(
      initial,
      lineOptions,
      planOptions,
      currencyOptions,
      true
    );
  }, [taxationLine?.id, lineOptions, planOptions, currencyOptions]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await getPlansByLine({
          variables: { lineID: taxationLine.id }
        });
        const plans = res?.data?.planConfig?.getActiveLinePlans ?? [];
        const mapped = plans.reduce((acc: Record<string, string>, p: any) => {
          acc[p.id] = `${p.code} - ${p.name}`;
          return acc;
        }, {});
        setPlanOptions(mapped);
      } catch (e) {
        toast.error(
          <ToastErrorMessage>
            {getError(e) || DEFAULT_ERROR_TEXT}
          </ToastErrorMessage>
        );
      }
    };

    loadPlans();
  }, [taxationLine?.id]);

  const submitForm = async (rawValues: Record<string, any>) => {
    const [values] = normalizeDynamicFormDrawerValues(sections, rawValues);

    try {
      const input = {
        taxationID: taxationId,
        lineID: Array.isArray(values.lineID) ? values.lineID[0] : values.lineID,
        planID: Array.isArray(values.planID) ? values.planID[0] : values.planID,
        fixedStamp: values.fixedStamp ? parseFloat(values.fixedStamp) : null,
        fixedStampCurrency: Array.isArray(values.fixedStampCurrency)
          ? values.fixedStampCurrency[0]
          : values.fixedStampCurrency,
        proportionalStamp: PrecisionService.divideBy100(
          values.proportionalStamp
        ),
        municipalityTax: PrecisionService.divideBy100(values.municipalityTax),
        taxOnCommission: PrecisionService.divideBy100(values.taxOnCommission),
        reinsuranceTax: PrecisionService.divideBy100(values.reinsuranceTax)
      };

      const res = await createPlanSpecific({
        planSpecificTaxationInput: input
      });

      if (!res.errors) {
        onSuccess();
      }
    } catch (err) {
      toast.error(
        <ToastErrorMessage>
          {getError(err) || DEFAULT_ERROR_TEXT}
        </ToastErrorMessage>
      );
    }
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
    />
  );
};

export { NewPlanSpecificTaxationDrawer };
